import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { combinarDataHora, separarDataHora, mascaraCEP } from '@/pages/assistente-evento/utils';
import { UFS } from '@/pages/assistente-evento/constants';

interface Props {
  eventoId: number;
  inicioEm: string | null;
  fimEm: string | null;
  localNome: string;
  endereco: string;
  cidade: string;
  uf: string;
  cep: string;
  aoFechar: () => void;
  aoSalvo: () => void;
}

const inputCls =
  'w-full min-w-0 rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

export default function QuandoOndeEditor({
  eventoId,
  inicioEm,
  fimEm,
  localNome,
  endereco,
  cidade,
  uf,
  cep,
  aoFechar,
  aoSalvo,
}: Props) {
  const ini = separarDataHora(inicioEm || '');
  const fim = separarDataHora(fimEm || '');

  const [inicioData, setInicioData] = useState(ini.data);
  const [inicioHora, setInicioHora] = useState(ini.hora);
  const [fimData, setFimData] = useState(fim.data);
  const [fimHora, setFimHora] = useState(fim.hora);
  const [local, setLocal] = useState(localNome);
  const [end, setEnd] = useState(endereco);
  const [cid, setCid] = useState(cidade);
  const [est, setEst] = useState(uf);
  const [cepVal, setCepVal] = useState(cep);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    if (!inicioData || !inicioHora) {
      setErro('Escolha a data e o horário de início.');
      return;
    }
    if (!cid.trim()) {
      setErro('Digite a cidade.');
      return;
    }
    const inicio = combinarDataHora(inicioData, inicioHora);
    let final = combinarDataHora(fimData, fimHora);
    if (inicio && !final) {
      final = new Date(new Date(inicio).getTime() + 3 * 3600 * 1000).toISOString();
    }
    if (inicio && final && new Date(final).getTime() <= new Date(inicio).getTime()) {
      setErro('O término precisa ser depois do início.');
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      const { error } = await supabase
        .from('eventos')
        .update({
          inicio_em: inicio,
          fim_em: final,
          local_nome: local.trim() || null,
          endereco: end.trim() || null,
          cidade: cid.trim(),
          uf: est,
          cep: cepVal.trim() || null,
        })
        .eq('id', eventoId);
      if (error) throw error;
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="space-y-5">
      {erro && (
        <p className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {erro}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3">
        <div className="rounded-lg border border-secondary-200 p-3">
          <span className="text-sm font-medium text-foreground-800">Começa</span>
          <div className="mt-2 grid grid-cols-[1fr_120px] gap-2">
            <input
              type="date"
              value={inicioData}
              onChange={(e) => setInicioData(e.target.value)}
              className={`${inputCls} cursor-pointer`}
            />
            <input
              type="time"
              value={inicioHora}
              onChange={(e) => setInicioHora(e.target.value)}
              className={`${inputCls} cursor-pointer`}
            />
          </div>
        </div>
        <div className="rounded-lg border border-secondary-200 p-3">
          <span className="text-sm font-medium text-foreground-800">Termina</span>
          <div className="mt-2 grid grid-cols-[1fr_120px] gap-2">
            <input
              type="date"
              value={fimData}
              onChange={(e) => setFimData(e.target.value)}
              className={`${inputCls} cursor-pointer`}
            />
            <input
              type="time"
              value={fimHora}
              onChange={(e) => setFimHora(e.target.value)}
              className={`${inputCls} cursor-pointer`}
            />
          </div>
        </div>
      </div>
      <p className="-mt-2 text-xs text-secondary-500">
        Sem término, a gente considera 3 horas depois do início.
      </p>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">Onde</span>
        <input
          type="text"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          className={`${inputCls} mt-1`}
          placeholder="Paróquia São João Batista"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">Endereço</span>
        <input
          type="text"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          className={`${inputCls} mt-1`}
          placeholder="Rua das Flores, 120 - Centro"
        />
      </label>

      <div className="grid grid-cols-[1fr_110px] gap-2">
        <label className="block">
          <span className="text-sm font-medium text-foreground-800">Cidade</span>
          <input
            type="text"
            value={cid}
            onChange={(e) => setCid(e.target.value)}
            className={`${inputCls} mt-1`}
            placeholder="Tupã"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-foreground-800">UF</span>
          <select
            value={est}
            onChange={(e) => setEst(e.target.value)}
            className={`${inputCls} mt-1 cursor-pointer`}
          >
            <option value="">UF</option>
            {UFS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-foreground-800">CEP</span>
        <input
          type="text"
          value={cepVal}
          onChange={(e) => setCepVal(mascaraCEP(e.target.value))}
          inputMode="numeric"
          className={`${inputCls} mt-1`}
          placeholder="17600-000"
        />
      </label>

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={aoFechar}
          disabled={salvando}
          className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}